import { authenticator } from 'otplib';
import { db, auth } from '../config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { signInWithCustomToken } from 'firebase/auth';

interface TempTOTPData {
  secret: string;
  createdAt: string;
}

class TOTPService {
  private static readonly ISSUER = 'Strategiz';
  private static readonly DIGITS = 6;
  private static readonly PERIOD = 30;
  private static readonly STORAGE_KEY = 'strategiz_totp_data';
  private static readonly EXPIRY_TIME = 10 * 60 * 1000; // 10 minutes in milliseconds

  private tempTOTPData: Map<string, TempTOTPData> = new Map();

  constructor() {
    this.loadTOTPData();
    authenticator.options = {
      digits: TOTPService.DIGITS,
      step: TOTPService.PERIOD,
      window: 1 // Allow 1 step before/after for time drift
    };
  }

  private loadTOTPData(): void {
    try {
      const savedData = localStorage.getItem(TOTPService.STORAGE_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        // Clean up expired data
        const now = new Date().getTime();
        Object.entries(parsedData).forEach(([email, data]) => {
          const createdAt = new Date((data as TempTOTPData).createdAt).getTime();
          if (now - createdAt < TOTPService.EXPIRY_TIME) {
            this.tempTOTPData.set(email, data as TempTOTPData);
          }
        });
        this.saveTOTPData(); // Save cleaned data back
      }
    } catch (error) {
      console.error('Error loading TOTP data from localStorage:', error);
      // Clear potentially corrupted data
      localStorage.removeItem(TOTPService.STORAGE_KEY);
    }
  }

  private saveTOTPData(): void {
    try {
      const dataToSave = Object.fromEntries(this.tempTOTPData.entries());
      localStorage.setItem(TOTPService.STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving TOTP data to localStorage:', error);
    }
  }

  async generateTOTP(email: string): Promise<{ secret: string; otpauthUrl: string }> {
    try {
      const secret = authenticator.generateSecret();
      
      // Generate otpauth URL with properly encoded parameters
      const issuer = encodeURIComponent(TOTPService.ISSUER);
      const account = encodeURIComponent(email);
      const otpauthUrl = `otpauth://totp/${issuer}:${account}?secret=${secret}&issuer=${issuer}&digits=${TOTPService.DIGITS}&period=${TOTPService.PERIOD}`;

      // Store temporary TOTP data
      this.tempTOTPData.set(email, {
        secret,
        createdAt: new Date().toISOString()
      });
      this.saveTOTPData();

      console.log('Generated TOTP for email:', email); // Debug log
      return { secret, otpauthUrl };
    } catch (error) {
      console.error('Error in generateTOTP:', error);
      throw new Error('Failed to generate TOTP secret');
    }
  }

  async verifyTOTP(email: string, token: string): Promise<boolean> {
    try {
      console.log('Verifying TOTP for email:', email);
      
      // First, check temporary storage (for registration flow)
      const tempData = this.tempTOTPData.get(email);
      if (tempData) {
        // Check if TOTP data has expired
        const createdAt = new Date(tempData.createdAt).getTime();
        const now = new Date().getTime();
        if (now - createdAt > TOTPService.EXPIRY_TIME) {
          console.log('Temporary TOTP data has expired for email:', email);
          this.tempTOTPData.delete(email);
          this.saveTOTPData();
        } else {
          // Verify using temporary secret
          const isValid = authenticator.verify({
            token,
            secret: tempData.secret
          });

          if (isValid) {
            // If valid during registration, save to permanent storage
            await setDoc(doc(db, 'users', email), {
              totp: {
                secret: tempData.secret,
                createdAt: new Date().toISOString(),
                lastVerifiedAt: new Date().toISOString()
              }
            }, { merge: true });

            // Clean up temporary data
            this.tempTOTPData.delete(email);
            this.saveTOTPData();
            return true;
          }
        }
      }

      // If no valid temporary data, check permanent storage (for login flow)
      const userDoc = await getDoc(doc(db, 'users', email));
      if (!userDoc.exists()) {
        console.error('No user document found for email:', email);
        return false;
      }

      const userData = userDoc.data();
      const secret = userData?.totp?.secret;

      if (!secret) {
        console.error('No TOTP secret found for email:', email);
        return false;
      }

      // Verify using permanent secret
      const isValid = authenticator.verify({
        token,
        secret
      });

      if (isValid) {
        // Update user document
        await setDoc(doc(db, 'users', email), {
          lastLoginAt: new Date().toISOString(),
          totp: {
            ...userData.totp,
            lastVerifiedAt: new Date().toISOString()
          }
        }, { merge: true });
      }

      return isValid;
    } catch (error) {
      console.error('Error in verifyTOTP:', error);
      throw new Error('Failed to verify TOTP code');
    }
  }

  async setupTOTP(email: string, secret: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', email);
      await setDoc(userRef, {
        email,
        totpSecret: secret,
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        authMethod: 'totp'
      }, { merge: true });
    } catch (error) {
      console.error('Error setting up TOTP:', error);
      throw new Error('Failed to set up TOTP');
    }
  }
}

export const totpService = new TOTPService();
